import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/lib/api';
import type { Envelope } from '@/lib/types';

/**
 * The bookings badge: how many PENDING bookings are waiting on this user right now.
 * Kept fresh by polling, same shape as the messages/notifications badges — only runs
 * while signed in; App starts it on authentication and stops it on sign-out. Polling
 * pauses while the tab is hidden so a backgrounded app is quiet.
 */
const POLL_MS = 20_000;

export const useBookingsStore = defineStore('bookings', () => {
  const pending = ref(0);
  let timer: number | null = null;

  async function refresh(): Promise<void> {
    try {
      pending.value = (await api.get<Envelope<{ pending: number }>>('/bookings/pending-count')).data.pending;
    } catch {
      // A failed poll (offline, 401 mid-logout) leaves the last known count.
    }
  }

  function start(): void {
    if (timer != null) return;
    void refresh();
    timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLL_MS);
  }

  function stop(): void {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
    pending.value = 0;
  }

  return { pending, refresh, start, stop };
});
